import './index.css';
import './styles/animations.css';

import { useEffect, useMemo, useState } from 'react';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import { ThemeProvider as LegacyThemeProvider } from '@mui/styles';
import { CssBaseline, GlobalStyles } from '@mui/material';

import App from './App';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { createAppTheme } from './Theme.js';
import useThemeStore from './stores/useThemeStore';

const isDarkModeEnabled = import.meta.env.VITE_ENABLE_DARK_MODE !== 'false';

function useSystemColorScheme() {
  const [systemMode, setSystemMode] = useState(() =>
    window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  );
  useEffect(() => {
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => setSystemMode(e.matches ? 'dark' : 'light');
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);
  return systemMode;
}

function Root() {
  const preference = useThemeStore((s) => s.preference);
  const hasHydrated = useThemeStore((s) => s.hasHydrated);
  const systemMode = useSystemColorScheme();

  const mode = isDarkModeEnabled
    ? preference === 'system' ? systemMode : preference
    : 'light';
  const theme = useMemo(() => createAppTheme(mode), [mode]);

  // Set data-theme on <html> so plain CSS can detect dark mode
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', mode);
  }, [mode]);

  if (!hasHydrated) {
    return null;
  }

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <LegacyThemeProvider theme={theme}>
        <CssBaseline />
        <GlobalStyles
          styles={(theme) => ({
            ':root': {
              colorScheme: theme.palette.mode,
              '--color-bg-default': theme.palette.background.default,
              '--color-bg-paper': theme.palette.background.paper,
              '--color-bg-subtle': theme.palette.background.subtle,
              '--color-text-primary': theme.palette.text.primary,
              '--color-text-secondary': theme.palette.text.secondary,
              '--color-text-disabled': theme.palette.text.disabled,
              '--color-divider': theme.palette.divider,
              '--color-border': theme.palette.custom.border,
              '--color-primary': theme.palette.primary.main,
              '--color-secondary': theme.palette.secondary.main,
              '--color-error': theme.palette.error.main,
              '--color-success': theme.palette.success.main,
              '--color-warning': theme.palette.warning.main,
              '--color-hover': theme.palette.action.hover,
              '--color-action-hover': theme.palette.action.hover,
              '--color-action-selected': theme.palette.action.selected,
              '--color-info': theme.palette.info.main,
              '--status-green-color': theme.palette.mode === 'dark' ? '#86efac' : '#059669',
              '--status-green-bg': theme.palette.mode === 'dark' ? 'rgba(21,128,61,0.15)' : '#ecfdf5',
              '--status-green-border': theme.palette.mode === 'dark' ? 'rgba(21,128,61,0.3)' : '#a7f3d0',
              '--status-red-color': theme.palette.mode === 'dark' ? '#fca5a5' : '#dc2626',
              '--status-red-bg': theme.palette.mode === 'dark' ? 'rgba(185,28,28,0.15)' : '#fef2f2',
              '--status-red-border': theme.palette.mode === 'dark' ? 'rgba(185,28,28,0.3)' : '#fecaca',
              '--status-amber-color': theme.palette.mode === 'dark' ? '#fbbf24' : '#856404',
              '--status-amber-bg': theme.palette.mode === 'dark' ? 'rgba(161,98,7,0.15)' : '#fff3cd',
              '--status-amber-border': theme.palette.mode === 'dark' ? 'rgba(161,98,7,0.3)' : '#ffeaa7',
              '--status-blue-color': theme.palette.mode === 'dark' ? '#93c5fd' : '#1d4ed8',
              '--status-blue-bg': theme.palette.mode === 'dark' ? 'rgba(29,78,216,0.15)' : '#eff6ff',
              '--status-blue-border': theme.palette.mode === 'dark' ? 'rgba(29,78,216,0.3)' : '#dbeafe',
              '--status-purple-color': theme.palette.mode === 'dark' ? '#c4b5fd' : '#7c3aed',
              '--status-purple-bg': theme.palette.mode === 'dark' ? 'rgba(124,58,237,0.15)' : '#f5f3ff',
              '--status-purple-border': theme.palette.mode === 'dark' ? 'rgba(124,58,237,0.3)' : '#ede9fe',
              '--color-ai-option-hover': theme.palette.mode === 'dark' ? 'rgba(148,75,168,0.2)' : '#f4edf6',
            },
          })}
        />
        <App />
        </LegacyThemeProvider>
      </ThemeProvider>
    </StyledEngineProvider>
  );
}

const container = document.getElementById('root');
const root = createRoot(container);

root.render(<Root />);
